import { Component, Injector, computed, effect, inject, untracked } from '@angular/core';
import { NgIf } from '@angular/common';
import { AccountDeletionService } from '../../services/account-deletion.service';
import { AppStatusStore } from '../../services/app-status.store';
import { bannerText, lockCopy, ACCESS_COPY, STOP_COPY } from '../../services/app-update';
import { showDeveloperModeStrip } from '../../services/developer-mode-strip.util';
import { WaitingChangesService } from '../../services/offline/waiting-changes.service';
// Was (2026-10-23, before the changes page showed the sign-out stop): import { SignOutStop, SignOutStopChoice, signOutStopCopy } from '../../services/offline/sign-out-stop';
import { ChangesPage } from '../../services/offline/changes-page';
import { OfflineWindowService } from '../../services/offline/offline-window.service';
import { UserService } from '../../services/user.service';

/**
 * The runtime's own status surfaces (Plan 7B): a thin grey "Offline mode" banner across the top (owner
 * Q11 — no pop-ups), a full-screen stop screen when a Live app is unpublished or too old (Q2, Q7), and a
 * small "Updated to the latest version" note after a soft update (Q1). Runtime builds only.
 *
 * It also carries the no-access screen (2026-09-21): signed in, but the gateway refuses this
 * person's own account row, so the app has nothing to show them. Same full-screen shape as the stop
 * screen and the same z-200, which is what puts it OVER the app's loading spinner (z-90 in
 * app.component.html) instead of behind it.
 *
 * Two access screens since 2026-09-24 — not granted yet, and removed.
 *
 * In an app that Works offline, a lock screen at day 7 without Zavo confirming the person (the
 * offline window), at the same layer. It sits under the stop and access screens, which are final.
 */
@Component({
  selector: 'app-status',
  standalone: true,
  imports: [NgIf],
  template: `
    <!-- Was (2026-10-23, before the banner opened the changes page):
    <div *ngIf="banner() as text" role="status"
         [class.top-0]="!belowStrip" [class.top-5]="belowStrip"
         class="fixed inset-x-0 z-[96] bg-gray-500 text-white text-[11px] font-medium text-center py-1">
      {{ text }}
    </div>
    -->
    <div *ngIf="banner() as text" role="status" (click)="openChanges()"
         [class.top-0]="!belowStrip" [class.top-5]="belowStrip"
         [class.cursor-pointer]="store.signedIn()"
         class="fixed inset-x-0 z-[96] bg-gray-500 text-white text-[11px] font-medium text-center py-1">
      {{ text }}
      <button *ngIf="store.signedIn()" type="button" (click)="openChanges(); $event.stopPropagation()"
              class="ml-2 underline underline-offset-2 text-white">See changes</button>
    </div>
    <div *ngIf="lock() as l" role="alertdialog" aria-live="assertive"
         class="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-2 bg-white p-8 text-center text-[#332D2C] dark:bg-gray-900 dark:text-gray-100">
      <h1 class="text-lg font-semibold">{{ l.title }}</h1>
      <p class="max-w-xs text-sm text-gray-500 dark:text-gray-400">{{ l.detail }}</p>
      <p *ngIf="l.waiting" class="max-w-xs text-sm font-medium text-[#332D2C] dark:text-gray-100">{{ l.waiting }}</p>
      <button type="button" (click)="reconnect()"
              class="mt-4 rounded-lg bg-gray-800 px-5 py-2 text-sm font-medium text-white dark:bg-gray-100 dark:text-gray-900">Try again</button>
    </div>
    <div *ngIf="stop() as s" role="alertdialog" aria-live="assertive"
         class="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-2 bg-white p-8 text-center text-[#332D2C]">
      <h1 class="text-lg font-semibold">{{ s.title }}</h1>
      <p class="max-w-xs text-sm text-gray-500">{{ s.detail }}</p>
      <button *ngIf="s.retry" type="button" (click)="retry()"
              class="mt-4 rounded-lg bg-gray-800 px-5 py-2 text-sm font-medium text-white">Try again</button>
    </div>
    <div *ngIf="access() as a" role="alertdialog" aria-live="assertive"
         class="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-2 bg-white p-8 text-center text-[#332D2C]">
      <h1 class="text-lg font-semibold">{{ a.title }}</h1>
      <p class="max-w-xs text-sm text-gray-500">{{ a.detail }}</p>
      <button *ngIf="canDeleteAccount()" type="button" data-test="no-access-delete" (click)="deleteAccount()"
              class="mt-4 text-sm font-medium text-gray-500 underline underline-offset-2">Delete my account</button>
    </div>
    <!-- Was (2026-10-23, before the changes page): the sign-out stop showed here, with only the count; the
         changes page now shows it above the Waiting changes it would remove.
        <div *ngIf="signOutStop() as q" role="alertdialog" aria-live="assertive"
         class="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-2 bg-white p-8 text-center text-[#332D2C] dark:bg-gray-900 dark:text-gray-100">
      <h1 class="text-lg font-semibold">{{ q.title }}</h1>
      <p class="max-w-xs text-sm text-gray-500 dark:text-gray-400">{{ q.detail }}</p>
      <div class="mt-4 flex gap-3">
        <button type="button" (click)="signOutChoice('go-back')"
                class="rounded-lg bg-gray-800 px-5 py-2 text-sm font-medium text-white dark:bg-gray-100 dark:text-gray-900">Go back</button>
        <button type="button" (click)="signOutChoice('sign-out-anyway')"
                class="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-[#332D2C] dark:border-gray-600 dark:text-gray-100">Sign out anyway</button>
      </div>
    </div>
    -->
    <div *ngIf="store.note() as n" role="status"
         class="fixed bottom-6 inset-x-0 z-[97] mx-auto w-max max-w-[90vw] rounded-full bg-gray-800/90 px-4 py-2 text-xs text-white">
      {{ n }}
    </div>
  `,
})
export class AppStatusComponent {
  readonly store = inject(AppStatusStore);
  // Created with the banner, so the changes kept before a restart are counted from the first screen.
  private readonly waiting = inject(WaitingChangesService);
  private readonly offlineWindow = inject(OfflineWindowService);
  private readonly users = inject(UserService);
  private readonly changesPage = inject(ChangesPage);
  private reconnecting = false;

  /** A draft in Developer mode already owns the top line; sit under it rather than on top of it. */
  readonly belowStrip = showDeveloperModeStrip((globalThis as any).ZAVO_RUNTIME);
  // Was (2026-10-23, before the banner counted Waiting and Unsaved changes and warned of the offline window):
  // readonly banner = computed(() => bannerText(this.store.online(), this.store.signedIn(), this.store.accountCheckUnavailable()));
  readonly banner = computed(() => bannerText(
    this.store.online(), this.store.signedIn(), this.store.accountCheckUnavailable(), this.store.waiting(),
    this.offlineWindow.state() === 'warn', this.store.unsavedUnseen(),
  ));
  /** Signed out, the sign-in screen must stay reachable: signing in online is itself what unlocks. */
  readonly lock = computed(() =>
    this.store.signedIn() && this.offlineWindow.state() === 'locked' ? lockCopy(this.store.waiting()) : null);
  /** Copy comes from the util; this component only renders it (ADR 0008). */
  readonly access = computed(() => {
    const v = this.store.noAccess();
    return v ? ACCESS_COPY[v] : null;
  });
  /** This screen covers the whole app, so the delete-my-account block on any page is out of reach behind it. */
  readonly canDeleteAccount = computed(() => this.store.noAccess() === 'not-yet');
  deleteAccount(): void { void this.injector.get(AccountDeletionService).deleteMyAccount(); }
  readonly stop = computed(() => {
    const r = this.store.stopped();
    return r ? STOP_COPY[r] : null;
  });
  private readonly injector = inject(Injector);
  /** A boolean, so a change in the waiting count alone does not ask Zavo again. */
  private readonly locked = computed(() => this.lock() !== null);

  constructor() {
    // Know the window from the first screen, not after the first minute.
    void this.offlineWindow.check();
    // Screens are not refreshed when the network returns, so nothing else would ask Zavo; the lock
    // screen asks once the phone is online again.
    effect(() => {
      if (this.locked() && this.store.online()) untracked(() => void this.reconnect());
    });
  }

  retry(): void { this.store.onRetry?.(); }
  // Was (2026-10-23, before the changes page showed the sign-out stop):
  // private readonly signOutStops = inject(SignOutStop);
  // readonly signOutStop = computed(() => {
  //   const q = this.signOutStops.asking();
  //   return q ? signOutStopCopy(q) : null;
  // });
  // signOutChoice(choice: SignOutStopChoice): void { this.signOutStops.answer(choice); }

  /** Signed out there are no changes of theirs to show. */
  openChanges(): void {
    if (this.store.signedIn()) void this.changesPage.open();
  }

  /** Asks Zavo for the person's own account: a good answer is a confirmed contact, which reopens the
   *  window. Offline, the read is answered on the phone and the lock simply stays. */
  async reconnect(): Promise<void> {
    if (this.reconnecting) return;
    this.reconnecting = true;
    try {
      await this.users.getCurrentUser();
    } finally {
      this.reconnecting = false;
      await this.offlineWindow.check();
    }
  }
}
